import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import { IdentifierType } from '../components/haccp/ClickableIdentifier';
import { ShipmentEntry } from '../types/shipment';
import { IngredientTraceCode } from '../types/ingredientTraceCode';
import { BatchRecord } from '../types/batchRecord';
import { BatchRecordIngredient } from '../types/batchRecordIngredient';
import { BakingProductionLog } from '../types/bakingProductionLog';
import { TraceCode } from '../types/traceCode';
import { DeliveryLog } from '../types/deliveryLog';

export interface TraceabilityResult {
  receivingEntries: ShipmentEntry[];
  ingredientTraceCodes: IngredientTraceCode[];
  batchRecords: BatchRecord[];
  batchRecordIngredients: BatchRecordIngredient[];
  bakingLogs: BakingProductionLog[];
  finishedProductTraceCodes: TraceCode[];
  deliveryLogs: DeliveryLog[];
}

export function useTraceabilityResolver(
  type: IdentifierType | null,
  value: string | null
): TraceabilityResult {
  const receivingEntries = useAppSelector(state => state.ingredientReceiving.entries);
  const ingredientTraceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);
  const batchRecords = useAppSelector(state => state.batchRecords.records);
  const batchRecordIngredients = useAppSelector(state => state.batchRecordIngredients.ingredients);
  const bakingLogs = useAppSelector(state => state.bakingProductionLog.logs);
  const finishedProductTraceCodes = useAppSelector(state => state.traceCodes.traceCodes);
  const deliveryLogs = useAppSelector(state => state.deliveryLogs.logs);

  return useMemo(() => {
    const empty: TraceabilityResult = {
      receivingEntries: [],
      ingredientTraceCodes: [],
      batchRecords: [],
      batchRecordIngredients: [],
      bakingLogs: [],
      finishedProductTraceCodes: [],
      deliveryLogs: []
    };

    if (!type || !value) return empty;

    if (type === 'lotNumber') {
      return resolveLotNumber(
        value,
        receivingEntries,
        ingredientTraceCodes,
        batchRecords,
        batchRecordIngredients,
        bakingLogs,
        finishedProductTraceCodes,
        deliveryLogs
      );
    }

    if (type === 'traceCode') {
      return resolveTraceCode(
        value,
        receivingEntries,
        ingredientTraceCodes,
        batchRecords,
        batchRecordIngredients,
        bakingLogs,
        finishedProductTraceCodes,
        deliveryLogs
      );
    }

    if (type === 'batchLotNumber') {
      return resolveBatchLotNumber(
        value,
        receivingEntries,
        ingredientTraceCodes,
        batchRecords,
        batchRecordIngredients,
        bakingLogs,
        finishedProductTraceCodes,
        deliveryLogs
      );
    }

    return empty;
  }, [
    type,
    value,
    receivingEntries,
    ingredientTraceCodes,
    batchRecords,
    batchRecordIngredients,
    bakingLogs,
    finishedProductTraceCodes,
    deliveryLogs
  ]);
}

function resolveLotNumber(
  lotNumber: string,
  allReceiving: ShipmentEntry[],
  allIngTraceCodes: IngredientTraceCode[],
  allBatchRecords: BatchRecord[],
  allBatchIngredients: BatchRecordIngredient[],
  allBakingLogs: BakingProductionLog[],
  allFinishedTraceCodes: TraceCode[],
  allDeliveryLogs: DeliveryLog[]
): TraceabilityResult {
  // 1. Find receiving entries with this lot number
  const receiving = allReceiving.filter(e => e.lotNumber === lotNumber);

  // 2. Find ingredient trace codes with this supplier lot number
  const ingTraceCodes = allIngTraceCodes.filter(tc => tc.supplierLotNumber === lotNumber);

  // 3. Find batch record ingredients containing this lot number
  const matchingBatchIngredients = allBatchIngredients.filter(
    bi => bi.lotNumbers && bi.lotNumbers.includes(lotNumber)
  );

  // 4. Find batch records from those ingredients
  const batchRecordIds = new Set(matchingBatchIngredients.map(bi => bi.batchRecordId));
  const batchRecords = allBatchRecords.filter(br => batchRecordIds.has(br.id));

  // 5. Find baking logs - match by lotNumber (baking logs use lotNumber = batchLotNumber)
  const batchLotNumbers = new Set(batchRecords.map(br => br.batchLotNumber));
  const bakingLogs = allBakingLogs.filter(log => batchLotNumbers.has(log.lotNumber));

  // 6. Find finished product trace codes via batchRecordId
  const finishedTraceCodes = allFinishedTraceCodes.filter(tc => batchRecordIds.has(tc.batchRecordId));

  // 7. Find delivery logs via batchRecordId
  const deliveryLogs = allDeliveryLogs.filter(dl => batchRecordIds.has(dl.batchRecordId));

  return {
    receivingEntries: receiving,
    ingredientTraceCodes: ingTraceCodes,
    batchRecords,
    batchRecordIngredients: matchingBatchIngredients,
    bakingLogs,
    finishedProductTraceCodes: finishedTraceCodes,
    deliveryLogs
  };
}

function resolveTraceCode(
  traceCode: string,
  allReceiving: ShipmentEntry[],
  allIngTraceCodes: IngredientTraceCode[],
  allBatchRecords: BatchRecord[],
  allBatchIngredients: BatchRecordIngredient[],
  allBakingLogs: BakingProductionLog[],
  allFinishedTraceCodes: TraceCode[],
  allDeliveryLogs: DeliveryLog[]
): TraceabilityResult {
  // 1. Find ingredient trace codes with this internal trace code
  const ingTraceCodes = allIngTraceCodes.filter(tc => tc.internalTraceCode === traceCode);

  // 2. Find receiving entries via ingredientReceivingId
  const receivingIds = new Set(
    ingTraceCodes.map(tc => tc.ingredientReceivingId).filter(Boolean) as string[]
  );
  const receiving = allReceiving.filter(e => receivingIds.has(e.id));

  // 3. Find batch record ingredients containing the supplier lot numbers
  const supplierLotNumbers = new Set(
    ingTraceCodes.map(tc => tc.supplierLotNumber).filter(Boolean) as string[]
  );
  const matchingBatchIngredients = allBatchIngredients.filter(
    bi => bi.lotNumbers && bi.lotNumbers.some(lot => supplierLotNumbers.has(lot))
  );

  // 4. Find batch records
  const batchRecordIds = new Set(matchingBatchIngredients.map(bi => bi.batchRecordId));
  const batchRecords = allBatchRecords.filter(br => batchRecordIds.has(br.id));

  // 5. Find baking logs
  const batchLotNumbers = new Set(batchRecords.map(br => br.batchLotNumber));
  const bakingLogs = allBakingLogs.filter(log => batchLotNumbers.has(log.lotNumber));

  // 6. Find finished product trace codes
  const finishedTraceCodes = allFinishedTraceCodes.filter(tc => batchRecordIds.has(tc.batchRecordId));

  // 7. Find delivery logs
  const deliveryLogs = allDeliveryLogs.filter(dl => batchRecordIds.has(dl.batchRecordId));

  return {
    receivingEntries: receiving,
    ingredientTraceCodes: ingTraceCodes,
    batchRecords,
    batchRecordIngredients: matchingBatchIngredients,
    bakingLogs,
    finishedProductTraceCodes: finishedTraceCodes,
    deliveryLogs
  };
}

function resolveBatchLotNumber(
  batchLotNumber: string,
  allReceiving: ShipmentEntry[],
  allIngTraceCodes: IngredientTraceCode[],
  allBatchRecords: BatchRecord[],
  allBatchIngredients: BatchRecordIngredient[],
  allBakingLogs: BakingProductionLog[],
  allFinishedTraceCodes: TraceCode[],
  allDeliveryLogs: DeliveryLog[]
): TraceabilityResult {
  // 1. Find batch records with this batch lot number
  const batchRecords = allBatchRecords.filter(br => br.batchLotNumber === batchLotNumber);
  const batchRecordIds = new Set(batchRecords.map(br => br.id));

  // 2. Find batch record ingredients for these batch records
  const matchingBatchIngredients = allBatchIngredients.filter(
    bi => batchRecordIds.has(bi.batchRecordId)
  );

  // 3. Collect all lot numbers from those ingredients
  const allLotNumbers = new Set<string>();
  matchingBatchIngredients.forEach(bi => {
    bi.lotNumbers?.forEach(lot => allLotNumbers.add(lot));
  });

  // 4. Find receiving entries matching those lot numbers
  const receiving = allReceiving.filter(e => allLotNumbers.has(e.lotNumber));

  // 5. Find ingredient trace codes matching those lot numbers
  const ingTraceCodes = allIngTraceCodes.filter(
    tc => tc.supplierLotNumber && allLotNumbers.has(tc.supplierLotNumber)
  );

  // 6. Find baking logs matching this batch lot number
  const bakingLogs = allBakingLogs.filter(log => log.lotNumber === batchLotNumber);

  // 7. Find finished product trace codes
  const finishedTraceCodes = allFinishedTraceCodes.filter(tc => batchRecordIds.has(tc.batchRecordId));

  // 8. Find delivery logs
  const deliveryLogs = allDeliveryLogs.filter(dl => batchRecordIds.has(dl.batchRecordId));

  return {
    receivingEntries: receiving,
    ingredientTraceCodes: ingTraceCodes,
    batchRecords,
    batchRecordIngredients: matchingBatchIngredients,
    bakingLogs,
    finishedProductTraceCodes: finishedTraceCodes,
    deliveryLogs
  };
}
